// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { capitalize } from '../../utils/helpers';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

export default function BudgetPeriodForm({ initial, onSubmit, onClose }) {
  const today = new Date();

  // Parse initial start_date if editing
  let initialMonth = MONTHS[today.getMonth()];
  let initialYear = today.getFullYear();
  let initialIncome = '0';

  if (initial?.start_date) {
    const date = new Date(initial.start_date);
    initialMonth = MONTHS[date.getMonth()];
    initialYear = date.getFullYear();
    initialIncome = initial.total_income || '0';
  }

  const [form, setForm] = useState({
    month: initialMonth,
    year: String(initialYear), // Ensure year is string for select consistency
    total_income: initialIncome,
    notes: initial?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setError(''); // Clear error when user starts editing
  };

  // Generate start and end dates from month/year
  const getMonthDates = (monthName, year) => {
    const monthIndex = MONTHS.indexOf(monthName);
    const start = new Date(year, monthIndex, 1);
    const end = new Date(year, monthIndex + 1, 0); // Last day of month
    return { start, end };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate total_income
    const income = parseFloat(form.total_income);
    if (!form.total_income || isNaN(income) || income <= 0) {
      setError('Total Income must be greater than 0');
      setLoading(false);
      return;
    }

    try {
      const { start, end } = getMonthDates(form.month, parseInt(form.year));
      const payload = {
        name: `${form.month} ${form.year}`,
        period_type: 'MONTHLY',
        start_date: start.toISOString().split('T')[0],
        end_date: end.toISOString().split('T')[0],
        total_income: income,
        notes: form.notes,
        is_active: false,
      };
      await onSubmit(payload);
    } catch (err) {
      setError(err.message || 'Failed to save period');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">
          {initial ? 'Edit' : 'New'} Budget Period
        </h2>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-400"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </p>
      )}

      {/* Month */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Month
        </label>
        <select
          value={form.month}
          onChange={set('month')}
          required
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {MONTHS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      {/* Year */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Year
        </label>
        <select
          value={form.year}
          onChange={set('year')}
          required
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {YEARS.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {/* Total Income */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Total Income / Budget <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          value={form.total_income}
          onChange={set('total_income')}
          placeholder="e.g., 120000"
          step="0.01"
          min="1"
          required
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <p className="text-xs text-gray-500 mt-1">Enter your total monthly income or budget (must be greater than 0)</p>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Notes (optional)
        </label>
        <textarea
          value={form.notes}
          onChange={set('notes')}
          placeholder="E.g., Extra income from bonus, planned vacation..."
          rows="3"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? 'Saving…' : 'Save Period'}
      </button>
    </form>
  );
}
